"use client";

import * as React from "react";
import { Box } from "../../components/ui/box";
import { computeFitCount } from "../../hooks/use-fit-count";

/**
 * Priority+ overflow for a tab strip: the tabs that fit keep their slot, and
 * the strip reports the values that no longer fit so the host can mark those
 * tabs hidden (`TabsTab overflowHidden`) and offer them through its own
 * "{n} more" control beside the strip.
 *
 * Composition, all inside one flex row:
 *
 *   <TabsOverflow order={values}>
 *     {({ hidden }) => (
 *       <>
 *         <TabsOverflowViewport>
 *           …the Tabs strip, every tab mounted, hidden ones marked…
 *         </TabsOverflowViewport>
 *         …the host's "{n} more" control, when `hidden` is non-empty…
 *         …any trailing rail controls…
 *         <TabsOverflowSpacer />
 *       </>
 *     )}
 *   </TabsOverflow>
 *
 * Every tab stays mounted: a hidden tab leaves the flow and paints nothing,
 * but keeps a real measurable width, which is what makes the fit arithmetic
 * possible without a second offscreen copy of the strip. The spacer is
 * load-bearing, not decoration: once tabs are hidden the viewport shrinks to
 * what it shows, so the viewport's own width no longer says how much room the
 * row HAS — the spacer soaks up the row's free space, and its width is the
 * budget for bringing tabs back. Without one the strip can shrink but never
 * recover.
 */

/**
 * Room held back for the host's "{n} more" control (plus its gap) the moment
 * the strip first overflows: the control is not in the row yet at that point,
 * so its width cannot be measured — it has to be reserved. Sized to the pill
 * control's widest plausible label ("99 more"). Once the control is mounted,
 * the row's real widths take over and no reserve applies.
 */
const DEFAULT_OVERFLOW_TRIGGER_RESERVE_PX = 72;

type TabsOverflowContextValue = {
  setSpacer: (node: HTMLElement | null) => void;
  setViewport: (node: HTMLElement | null) => void;
};

const TabsOverflowContext =
  React.createContext<TabsOverflowContextValue | null>(null);

export type TabsOverflowState = {
  /** Values from `order` whose tabs lost their slot, in display order. */
  hidden: string[];
};

export type TabsOverflowProps = {
  /** Tab values in display order — the same array the strip renders from, so
   *  measured widths line up with values by index. */
  order: string[];
  /** Width held for the host's overflow control before it mounts. Sized to
   *  the default pill's widest plausible label ("99 more"). */
  overflowReserve?: number;
  children: (state: TabsOverflowState) => React.ReactNode;
};

export function TabsOverflow({
  order,
  overflowReserve = DEFAULT_OVERFLOW_TRIGGER_RESERVE_PX,
  children,
}: TabsOverflowProps): React.ReactElement {
  const [viewport, setViewport] = React.useState<HTMLElement | null>(null);
  const [spacer, setSpacer] = React.useState<HTMLElement | null>(null);
  const [visibleCount, setVisibleCount] = React.useState(order.length);

  React.useLayoutEffect(() => {
    if (!viewport) return undefined;

    const measure = () => {
      const tabs = Array.from(
        viewport.querySelectorAll<HTMLElement>('[data-slot="tabs-tab"]'),
      );
      const list = viewport.querySelector<HTMLElement>(
        '[data-slot="tabs-list"]',
      );
      const gap =
        Number.parseFloat(list ? getComputedStyle(list).columnGap : "") || 0;
      // The spacer holds the row's free space; +1 absorbs subpixel rounding.
      // The viewport's padding is the focus-ring bleed (see the viewport),
      // not room for tabs, so it comes off the width tabs may fill.
      const viewportStyle = getComputedStyle(viewport);
      const bleed =
        (Number.parseFloat(viewportStyle.paddingLeft) || 0) +
        (Number.parseFloat(viewportStyle.paddingRight) || 0);
      const capacity =
        viewport.clientWidth - bleed + (spacer?.clientWidth ?? 0) + 1;
      const widths = tabs.map((tab) => tab.offsetWidth);
      // In-flow furniture sharing the list — a rail separator between a
      // pinned tab and the sortables — holds a slot the fold can never
      // reclaim, so its width (plus the gap each piece adds) is charged up
      // front; skipping it clips the last "fitting" tab by exactly that much.
      // Out-of-flow children hold no slot: hidden tabs' absolute boxes and
      // the active-tab indicator.
      const furnitureWidth = list
        ? Array.from(list.children)
            .filter(
              (child): child is HTMLElement =>
                child instanceof HTMLElement &&
                child.getAttribute("data-slot") !== "tabs-tab" &&
                getComputedStyle(child).position !== "absolute",
            )
            .reduce((sum, child) => sum + child.offsetWidth + gap, 0)
        : 0;
      const total =
        widths.reduce((sum, width) => sum + width, 0) +
        gap * (tabs.length - 1) +
        furnitureWidth;

      // A committed hidden tab means the host's own control is already in
      // the row — its width is real and `capacity` excludes it. Before that
      // commit, room is held for the control it is about to mount. The
      // strip never empties: one clipped tab beats a bare rail.
      const controlMounted = viewport.querySelector("[data-tab-overflow]") !== null;
      const next =
        total > capacity
          ? computeFitCount({
              widths,
              capacity,
              gap,
              fixed: furnitureWidth,
              reserve: () => (controlMounted ? 0 : overflowReserve),
              min: 1,
            })
          : tabs.length;
      setVisibleCount(next);
    };

    const resizeObserver = new ResizeObserver(measure);
    const observe = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(viewport);
      if (spacer) resizeObserver.observe(spacer);
      for (const tab of Array.from(
        viewport.querySelectorAll<HTMLElement>('[data-slot="tabs-tab"]'),
      )) {
        resizeObserver.observe(tab);
      }
    };
    // Tabs mount, unmount, and swap elements (a rename swaps the label for a
    // field), and a swapped element silently drops out of the resize
    // observation — re-collect whenever the subtree changes.
    const mutationObserver = new MutationObserver(() => {
      observe();
      measure();
    });
    mutationObserver.observe(viewport, { childList: true, subtree: true });
    observe();
    measure();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [viewport, spacer, overflowReserve]);

  const context = React.useMemo(() => ({ setSpacer, setViewport }), []);
  // Clamped, not trusted: `order` can change under a stale count until the
  // mutation observer's re-measure lands.
  const hidden = React.useMemo(
    () => order.slice(Math.min(visibleCount, order.length)),
    [order, visibleCount],
  );

  return (
    <TabsOverflowContext.Provider value={context}>
      {children({ hidden })}
    </TabsOverflowContext.Provider>
  );
}

export type TabsOverflowViewportProps = {
  children: React.ReactNode;
};

/**
 * The strip's clip box. Hidden tabs sit inside it out of the flow, so its
 * width is exactly the visible run — the "{n} more" control beside it hugs
 * the last visible pill instead of drifting to the row's far edge.
 */
export function TabsOverflowViewport({
  children,
}: TabsOverflowViewportProps): React.ReactElement {
  const context = React.useContext(TabsOverflowContext);
  return (
    <Box
      ref={context?.setViewport}
      data-slot="tabs-overflow-viewport"
      direction="row"
      minWidth={0}
      // The clip is HORIZONTAL only, and `clip` rather than `hidden`: the
      // underline indicator hangs 1px below the list to ride the hairline
      // under the strip, so a vertical clip amputates the bar's bottom pixel;
      // and `hidden` makes this a scroll container, which focus-scrolling a
      // folded tab into view could silently shift.
      overflowX="clip"
      // The clip edge sits 4px OUTSIDE the strip on each side: a tab's focus
      // ring paints 2px past its box, and the box itself reaches 1px past the
      // list (`-mx-px`), so a clip flush with the strip amputates the first
      // and last tabs' rings. Padding pushes the strip back to where it was,
      // so the tabs' positions, and the row's width, are unchanged — the
      // measure above subtracts this padding from the fillable width.
      marginXNegative="xs"
      paddingX="xs"
      // Anchors the hidden tabs' absolute boxes so they never land outside
      // the clip.
      position="relative"
    >
      {children}
    </Box>
  );
}

/**
 * Where the row's spare width lives. Place it after the strip's trailing
 * controls: it grows into whatever the row has left, and that width is what
 * lets hidden tabs come back when the row widens.
 */
export function TabsOverflowSpacer(): React.ReactElement {
  const context = React.useContext(TabsOverflowContext);
  return (
    <Box
      ref={context?.setSpacer}
      aria-hidden
      data-slot="tabs-overflow-spacer"
      grow
      minWidth={0}
    />
  );
}
